// NovaBank onboarding — UI constants (dropdowns, product info, promos).
// Data here is display content, not backend state. Session, OTPs and
// application state all come from the real API (src/api/onboarding.ts).
import type { ProductType } from '@/types/onboarding.types';

export const OTP_RESEND_SECONDS = 30;
export const OTP_EXPIRY_SECONDS = 180; // Phase 5 OTP expiry is 180 seconds.
export const HELPLINE = '1800 266 4321';

export interface ProductConfig {
  type: ProductType;
  name: string;
  description: string;
  bullets: string[];
  readMoreBullets: string[];
  fees: string;
  whoCanApply: string[];
}

export const PRODUCTS: ProductConfig[] = [
  {
    type: 'SAVINGS',
    name: 'Savings Account',
    description: 'The everyday account that earns interest on your balance.',
    bullets: [
      '3.5% p.a. interest, paid quarterly',
      '₹5,000 average monthly balance — zero-balance basic variant available',
      'Free RuPay / Visa debit card',
      'Free UPI, IMPS and NEFT',
    ],
    readMoreBullets: [
      '5 free ATM withdrawals a month',
      'Nominee facility',
      'Opening deposit ₹1,000',
    ],
    fees: '₹0 monthly when you keep the balance — non-maintenance charge up to ₹500.',
    whoCanApply: ['18 years or older', 'Resident Indian', 'Aadhaar and PAN'],
  },
  {
    type: 'CURRENT',
    name: 'Current Account',
    description: 'Built for businesses, traders and the self-employed.',
    bullets: [
      'Unlimited transactions, no interest',
      '₹10,000 average monthly balance',
      'Free cash deposit up to ₹2 lakh a month',
      'Free NEFT, RTGS and IMPS',
    ],
    readMoreBullets: [
      'Free cheque book — 50 leaves a quarter',
      'Overdraft facility on request',
      'Opening deposit ₹10,000',
    ],
    fees: '₹0 monthly when you keep the balance — non-maintenance charge up to ₹1,000.',
    whoCanApply: [
      '18 years or older',
      'Resident Indian',
      'Aadhaar and PAN',
      'Business proof (GST, trade licence or similar)',
    ],
  },
  {
    type: 'SALARY',
    name: 'Salary Account',
    description: 'The zero-balance account your salary lands in.',
    bullets: [
      'Zero minimum balance',
      '3.5% p.a. interest, same as Savings',
      'Free platinum debit card',
      'Personal accident cover of ₹10 lakh',
    ],
    readMoreBullets: [
      'Free NEFT, RTGS and IMPS',
      'Unlimited free ATM withdrawals',
      'Converts to regular savings if no salary credit for 3 months',
    ],
    fees: '₹0 — no monthly or non-maintenance charges.',
    whoCanApply: [
      '18 years or older',
      'Resident Indian',
      'Aadhaar and PAN',
      'Employer details',
    ],
  },
];

export const INTRO_SLIDES = [
  {
    headline: 'Open an account\nin minutes',
    subheadline: 'Paperless KYC and instant activation, all from your phone.',
  },
  {
    headline: 'Send money\nin seconds',
    subheadline: 'UPI, IMPS and transfers that just work, day or night.',
  },
  {
    headline: 'Your card,\nyour rules',
    subheadline: 'Set limits, freeze and unfreeze — total control in your hands.',
  },
];

// Assisted-by employee / agent codes → representative name (display lookup).
export const AGENT_CODES: Record<string, string> = {
  NB1042: 'Priya Nair',
  NB2077: 'Rohan Mehta',
  RM0311: 'Ananya Iyer',
  DSA4521: 'Karthik Reddy',
  BC7788: 'Sunita Devi',
};

export interface Branch {
  label: string;
  ifsc: string;
}

export const BRANCHES: Branch[] = [
  { label: 'Indiranagar, Bengaluru', ifsc: 'NOVA0000001' },
  { label: 'Koramangala, Bengaluru', ifsc: 'NOVA0000002' },
  { label: 'MG Road, Bengaluru', ifsc: 'NOVA0000003' },
  { label: 'Whitefield, Bengaluru', ifsc: 'NOVA0000004' },
  { label: 'Bandra West, Mumbai', ifsc: 'NOVA0000011' },
  { label: 'Andheri East, Mumbai', ifsc: 'NOVA0000012' },
  { label: 'Connaught Place, New Delhi', ifsc: 'NOVA0000021' },
  { label: 'Dwarka, New Delhi', ifsc: 'NOVA0000022' },
  { label: 'T. Nagar, Chennai', ifsc: 'NOVA0000031' },
  { label: 'Anna Nagar, Chennai', ifsc: 'NOVA0000032' },
  { label: 'Banjara Hills, Hyderabad', ifsc: 'NOVA0000041' },
  { label: 'Salt Lake, Kolkata', ifsc: 'NOVA0000051' },
  { label: 'Koregaon Park, Pune', ifsc: 'NOVA0000061' },
  { label: 'Navrangpura, Ahmedabad', ifsc: 'NOVA0000071' },
  { label: 'Marine Drive, Kochi', ifsc: 'NOVA0000081' },
];

// PIN → city/state lookup for the address section.
export const PIN_LOOKUP: Record<string, { city: string; state: string }> = {
  '560001': { city: 'Bengaluru', state: 'Karnataka' },
  '560038': { city: 'Bengaluru', state: 'Karnataka' },
  '400001': { city: 'Mumbai', state: 'Maharashtra' },
  '400050': { city: 'Mumbai', state: 'Maharashtra' },
  '110001': { city: 'New Delhi', state: 'Delhi' },
  '600001': { city: 'Chennai', state: 'Tamil Nadu' },
  '500001': { city: 'Hyderabad', state: 'Telangana' },
  '700001': { city: 'Kolkata', state: 'West Bengal' },
  '411001': { city: 'Pune', state: 'Maharashtra' },
  '380001': { city: 'Ahmedabad', state: 'Gujarat' },
  '302001': { city: 'Jaipur', state: 'Rajasthan' },
  '682001': { city: 'Kochi', state: 'Kerala' },
};

export const STATES_AND_UTS = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya',
  'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim',
  'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand',
  'West Bengal', 'Andaman and Nicobar Islands', 'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Jammu and Kashmir',
  'Ladakh', 'Lakshadweep', 'Puducherry',
];

// Promo carousel on the applicant home.
export const PROMOS = [
  {
    title: 'Fixed deposits up to 7.25% p.a.',
    subtitle: 'Start from ₹5,000 and break it any time, right from the app.',
  },
  {
    title: 'NovaBank Rewards credit card',
    subtitle: 'Lifetime free, with 5% back on your everyday UPI spends.',
  },
  {
    title: 'Personal loan in 10 minutes',
    subtitle: 'Pre-approved offers up to ₹5 lakh, money straight to you.',
  },
  {
    title: 'Refer a friend, get ₹250',
    subtitle: 'They open an account, you both get a thank-you credit.',
  },
];
